import { useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Image,
  type ImageSourcePropType,
} from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList } from "../../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { CardSurface, MoonSearchInput, BackLink } from "@louise/moon-ui";
import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  GLOSSARY_TERMS,
  type GlossaryTerm,
} from "../CostsScreen/glossary.data";
import { useTranslations } from "@src/packages/i18n";
import { useSmartBack } from "../../hooks/useSmartBack";
import ScreenTitle from "../../components/ScreenTitle";

type Props = NativeStackScreenProps<CostsStackParamList, "Glossary">;

const DEFAULT_GLOSSARY_ILLUSTRATION: ImageSourcePropType = require("../../../assets/glossary/deductible.png");
const GLOSSARY_ILLUSTRATIONS: Record<string, ImageSourcePropType> = {
  deductible: DEFAULT_GLOSSARY_ILLUSTRATION,
  "oop-max": require("../../../assets/glossary/out-of-pocket-maximum.png"),
  coinsurance: require("../../../assets/glossary/coinsurance.png"),
  copay: require("../../../assets/glossary/copay.png"),
  eob: require("../../../assets/glossary/eob.png"),
  "prior-auth": require("../../../assets/glossary/prior-authorization.png"),
  "in-network": require("../../../assets/glossary/in-network.png"),
  "lifetime-max": require("../../../assets/glossary/lifetime-maximum.png"),
  "allowed-amount": require("../../../assets/glossary/allowed-amount.png"),
  claim: require("../../../assets/glossary/claim.png"),
  premium: require("../../../assets/glossary/premium.png"),
  "out-of-network": require("../../../assets/glossary/out-of-network.png"),
  "balance-billing": require("../../../assets/glossary/balance-billing.png"),
  "excluded-services": require("../../../assets/glossary/excluded-services.png"),
  "patient-responsibility": require("../../../assets/glossary/patient-responsibility.png"),
};

export default function GlossaryScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const [search, setSearch] = useState("");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });

  const filteredTerms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return GLOSSARY_TERMS;
    return GLOSSARY_TERMS.filter(
      (t) =>
        t.term.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q),
    );
  }, [search]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Shared secondary-screen header */}
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          accessibilityLabel={
            route.params?.returnTab
              ? t("you.shared.back")
              : t("costs.back_to_costs")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <ScreenTitle>Insurance glossary</ScreenTitle>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 18,
          }}
        >
          Understand the terms used in your coverage
        </Text>

        {/* Search */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search terms…"
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginBottom: 14 }}
        />

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textFaint,
            marginBottom: 10,
            paddingHorizontal: 2,
          }}
        >
          {filteredTerms.length} {filteredTerms.length === 1 ? "term" : "terms"}
        </Text>

        {/* Terms */}
        {filteredTerms.length === 0 ? (
          <SearchEmptyState
            title={t("empty_states.no_terms_match_search")}
            onReset={() => setSearch("")}
          />
        ) : (
          <View style={{ gap: 12 }}>
            {filteredTerms.map((term) => (
              <TermCard
                key={term.id}
                term={term}
                illustration={
                  GLOSSARY_ILLUSTRATIONS[term.id] ??
                  DEFAULT_GLOSSARY_ILLUSTRATION
                }
              />
            ))}
          </View>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}

function TermCard({
  term,
  illustration,
}: {
  term: GlossaryTerm;
  illustration: ImageSourcePropType;
}) {
  return (
    <CardSurface style={{ padding: 18 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 14,
        }}
      >
        <Image
          source={illustration}
          accessibilityIgnoresInvertColors
          resizeMode="contain"
          style={{ width: 76, height: 76, flexShrink: 0 }}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 16,
              fontWeight: "600",
              color: EMBER.colors.text,
              marginBottom: 8,
            }}
          >
            {term.term}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 21,
            }}
          >
            {term.definition}
          </Text>
        </View>
      </View>
    </CardSurface>
  );
}
